import reactUpdate from 'immutability-helper';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import {MixtureIngredientsStructuredTable} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {
  getParentMixtureIdFromTableTypedValueOnly,
  MixtureMetadataStructuredTable,
} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {
  resyncMixtureGroupComponentsAsync,
  doesMixtureGroupRequireResync,
  reduceGroupHandleParentMixtureChange,
  reduceGroupHandleUpdatedMixturePrepAmount,
} from 'notebook/integrations/registry/MixtureNoteLinkedGroupReducers';
import {getMixtureMetadataStructuredTableAdapter} from 'notebook/integrations/registry/MixtureTableAdapters';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import addMixtureGroupToWorklistActionFunction from 'notebook/notes/groups/action-functions/addMixtureGroupToWorklistActionFunction';
import createRequestForMixtureGroupActionFunction from 'notebook/notes/groups/action-functions/createRequestForMixtureGroupActionFunction';
import {
  downloadIngredientsCSVActionFunction,
  downloadMixtureCSVActionFunction,
} from 'notebook/notes/groups/action-functions/downloadMixtureGroupCSVActionFunctions';
import insertParentMixtureInstructionsActionFunction from 'notebook/notes/groups/action-functions/insertParentMixtureInstructionsActionFunction';
import printMixtureGroupBarcodeActionFunction from 'notebook/notes/groups/action-functions/printMixtureGroupBarcodeActionFunction';
import refreshMixtureNoteLinkedGroupActionFunction from 'notebook/notes/groups/action-functions/refreshMixtureNoteLinkedGroupActionFunction';
import submitMixtureGroupActionFunction from 'notebook/notes/groups/action-functions/submitMixtureGroupActionFunction';
import GenericNoteLinkedGroupAdapter from 'notebook/notes/groups/GenericNoteLinkedGroupAdapter';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
  NoteLinkedGroup,
  NoteLinkedGroupKind,
  NoteLinkedGroupResyncBannerContent,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {
  findMatchingNotePartInGroup,
  reduceGroupUpdateNotePart,
} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import {SamplesSource} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SampleFilterType} from 'notebook/sample-propagation/SampleFilter';
import {hydrateBioentityFromSampleId} from 'notebook/sample-propagation/util/hydrateSampleId';
import {makeBioentitySampleDetails} from 'notebook/sample-propagation/util/makeSampleDetails';
import {EXISTING_BIOENTITY_COLUMN_NAME} from 'notebook/structured-tables/columns/ColumnDefinitions';
import remergeStructuredTableSingleInputs from 'notebook/structured-tables/single-inputs/util/remergeStructuredTableSingleInputs';
import {
  GenericStructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {
  isMixtureIngredientsStructuredTable,
  isMixtureMetadataStructuredTable,
  isNotePartWithMixtureMetadataStructuredTable,
} from 'notebook/TypePredicates';
import {isNotePartWithItem, NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';
import {MaybePromise} from 'util/Promises';

export const GroupKind = 'MIXTURE_GROUP';
export type GroupKind = typeof GroupKind;

export type MixtureGroup = NoteLinkedGroup<MixtureGroupMetadata>;

/**
 * Applies the Single Input column configuration used by Mixture Prep metadata tables when
 * {@link BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION} is on: every column is marked as
 * a Single Input and the first row’s cells are remerged to span all rows in their column.
 * Shared by the table-creation path in {@link MixtureNoteLinkedGroupAdapter.createNoteParts} and
 * by the legacy self-heal in {@link MixtureNoteLinkedGroupAdapter.detectAndUpdateLegacyMixtureGroup}
 * so both produce identical metadata table shapes.
 */
export function configureMixtureMetadataSingleInputColumns<
  T extends {columns: Array<GenericStructuredTableColumn<unknown>>; rows: Array<StructuredTableRow<unknown>>},
>(metadataTable: T): T {
  const withSingleInputColumns = {
    ...metadataTable,
    columns: metadataTable.columns.map((column) => ({...column, isSingleInput: true as const})),
  };
  return remergeStructuredTableSingleInputs(withSingleInputColumns) as T;
}

export class MixtureNoteLinkedGroupAdapter extends GenericNoteLinkedGroupAdapter<
  MixtureGroupMetadata,
  CreateMixtureGroupExtras
> {
  getDisplayName(mixtureGroup: MixtureGroup): string {
    return 'Create mixture prep';
  }

  override supportsCopyPaste(): boolean {
    return false;
  }

  override requiresTableEvaluationOnInsert(): boolean {
    return BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION;
  }

  override supportsEntryLevelEvaluation(): boolean {
    return BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION;
  }

  override getActionFunctions(): GroupActionFunctions<MixtureGroupMetadata, CreateMixtureGroupExtras> {
    const inherited = super.getActionFunctions();
    return {
      primary: submitMixtureGroupActionFunction,
      secondary: [
        insertParentMixtureInstructionsActionFunction,
        refreshMixtureNoteLinkedGroupActionFunction,
        createRequestForMixtureGroupActionFunction,
        addMixtureGroupToWorklistActionFunction,
        printMixtureGroupBarcodeActionFunction,
      ],
      overflow: [
        // TODO: Figure out if this use of ! is safe.
        ...inherited.overflow!,
        downloadMixtureCSVActionFunction,
        downloadIngredientsCSVActionFunction,
      ],
      footer: inherited.footer ?? [],
    };
  }

  createNoteParts(
    createProps: CreateNoteLinkedGroupProps<MixtureGroupMetadata, CreateMixtureGroupExtras>
  ): Array<NotePart> {
    const {name, kind, schemas, entryId, selectorsById, isConfig, extras} = createProps;
    assert(kind === NoteLinkedGroupKind.MIXTURE_GROUP, 'This adapter only works for Mixture Groups');
    assert(schemas.length === 1, 'Mixture group only allows one schema');
    const [schema] = schemas;

    const baseMetadataTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.MIXTURE_METADATA).createTable({
      name: extras.getNextUniqueName(`${name} mixture`, {allowNoSuffix: true}),
      schemas: [{...schema, columnGroup: null}],
      entryId,
      metadata: extras.mixtureMetadataTableMetadata,
      selectorsById,
      isConfig,
      structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
    });
    const metadataTable = BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION
      ? configureMixtureMetadataSingleInputColumns(baseMetadataTable)
      : baseMetadataTable;
    const ingredientsTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.MIXTURE_INGREDIENTS).createTable(
      {
        name: extras.getNextUniqueName(`${name} ingredients`, {allowNoSuffix: true}),
        schemas: [{...schema, columnGroup: null}],
        entryId,
        metadata: {
          searchAllSchemas: true,
        },
        selectorsById,
        isConfig,
        structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
      }
    );

    // @ts-expect-error(2322): This is passing in `shouldGenerateCid: false` but we are expecting it to have a cid
    return [metadataTable, ingredientsTable].map((structuredTable) =>
      reactUpdate(makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT, null, {shouldGenerateCid: false}), {
        // @ts-expect-error(2322): The NotePart StructuredTable mapping uses unknown instead of a type parameter
        item: {$set: structuredTable},
      })
    );
  }

  override reduceGroup(
    noteLinkedGroup: MixtureGroup,
    updatedNotePart:
      | NotePartWithItem<MixtureMetadataStructuredTable>
      | NotePartWithItem<MixtureIngredientsStructuredTable>
  ): MaybePromise<MixtureGroup> {
    // We always want to at least update the note part that changed in the group.
    // NOTE: It's important to do this update first and then pass the result along to the other reducers,
    // because reduceGroupHandleParentMixtureChange may make further changes to `updatedNotePart` and
    // incorporate that version into the group, so we need to avoid overriding that.
    const withBasicUpdate = reduceGroupUpdateNotePart<MixtureGroupMetadata>(noteLinkedGroup, updatedNotePart);

    if (isNotePartWithMixtureMetadataStructuredTable(updatedNotePart)) {
      // The only changes that we need to propagate updates from would be in the note part corresponding to
      // the mixture metadata table
      const currentNotePart = findMatchingNotePartInGroup(
        noteLinkedGroup,
        updatedNotePart
      ) as NotePartWithItem<MixtureMetadataStructuredTable>;
      assert(
        currentNotePart != null,
        `Expected a matching note part for updatedNotePart: type=${updatedNotePart.type}, cid=${updatedNotePart.cid}`
      );

      const updatedForAmount = reduceGroupHandleUpdatedMixturePrepAmount(
        withBasicUpdate,
        currentNotePart,
        updatedNotePart
      );
      return reduceGroupHandleParentMixtureChange(updatedForAmount, currentNotePart, updatedNotePart);
    } else {
      return withBasicUpdate;
    }
  }

  override prepareGroupForStore(mixtureGroup: MixtureGroup): void {
    this.detectAndUpdateLegacyMixtureGroup(mixtureGroup);
  }

  override isGroupResyncRequired(mixtureGroup: MixtureGroup, evaluatedTableMap: OutputTableMap): boolean {
    return doesMixtureGroupRequireResync(mixtureGroup, evaluatedTableMap);
  }

  override resyncGroupComponentsAsync(
    mixtureGroup: MixtureGroup,
    evaluatedTableMap: OutputTableMap
  ): MaybePromise<MixtureGroup> {
    return resyncMixtureGroupComponentsAsync(mixtureGroup, evaluatedTableMap);
  }

  override getResyncBannerContent(): NoteLinkedGroupResyncBannerContent {
    return {
      title: 'Mixture metadata updated',
      subtitle: 'Apply changes to recalculate ingredients.',
      buttonText: 'Apply changes',
    };
  }

  /**
   * Self-heals legacy Mixture Prep groups loaded from storage so their metadata table is ready for
   * Single Input rendering without dispatching an editor mutation or triggering a save.
   *
   * New Mixture Prep groups are created with the Single Input configuration already applied
   * (see {@link createNoteParts}), so this self-heal is only needed for groups that were saved
   * before the configuration was applied at insert time.
   */
  private detectAndUpdateLegacyMixtureGroup(mixtureGroup: MixtureGroup): void {
    if (!BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION) {
      return;
    }
    const metadataTableNotePart = this.getMixtureMetadataNotePart(mixtureGroup);
    const metadataTable = metadataTableNotePart.item;
    const isLegacyMetadataTable = metadataTable.columns.some((column) => !column.isSingleInput);
    if (!isLegacyMetadataTable) {
      return;
    }
    const parentMixtureId = getParentMixtureIdFromTableTypedValueOnly(metadataTable);
    metadataTableNotePart.item = configureMixtureMetadataSingleInputColumns(metadataTable);
    if (parentMixtureId != null) {
      const ingredientsTableNotePart = this.getMixtureIngredientsNotePart(mixtureGroup);
      ingredientsTableNotePart.item = {
        ...ingredientsTableNotePart.item,
        metadata: {
          ...ingredientsTableNotePart.item.metadata,
          parentMixtureId,
        },
      };
    }
  }

  getTagSchema(group: MixtureGroup): TagSchema | null {
    return getMixtureMetadataStructuredTableAdapter().getTagSchema(
      this.getMixtureMetadataStructuredTable(group)
    );
  }

  getMixtureMetadataNotePart(mixtureGroup: MixtureGroup): NotePartWithItem<MixtureMetadataStructuredTable> {
    const metadataTableNotePart = mixtureGroup.containedNoteParts[0];
    assert(
      isNotePartWithItem(metadataTableNotePart) &&
        isMixtureMetadataStructuredTable(metadataTableNotePart.item),
      'First note part in group must be mixture metadata table'
    );
    return metadataTableNotePart as NotePartWithItem<MixtureMetadataStructuredTable>;
  }

  getMixtureMetadataStructuredTable(mixtureGroup: MixtureGroup): MixtureMetadataStructuredTable {
    return this.getMixtureMetadataNotePart(mixtureGroup).item;
  }

  getMixtureIngredientsNotePart(
    mixtureGroup: MixtureGroup
  ): NotePartWithItem<MixtureIngredientsStructuredTable> {
    const ingredientsTableNotePart = mixtureGroup.containedNoteParts[1];
    assert(
      isNotePartWithItem(ingredientsTableNotePart) &&
        isMixtureIngredientsStructuredTable(ingredientsTableNotePart.item),
      'Second note part in group must be ingredients table'
    );
    return ingredientsTableNotePart as NotePartWithItem<MixtureIngredientsStructuredTable>;
  }

  getMixtureIngredientsStructuredTable(mixtureGroup: MixtureGroup): MixtureIngredientsStructuredTable {
    return this.getMixtureIngredientsNotePart(mixtureGroup).item;
  }

  // TODO(BNCH-150881): Once ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is permanent, make `evaluatedTableMap`
  // non-nullable (drop `| null`, mirroring the metadata adapter's getMixtureId computedTable param) so the
  // raw-cell fallback can be deleted there.
  getMixtureId(group: MixtureGroup, evaluatedTableMap: OutputTableMap | null): string | null {
    const metadataTable = this.getMixtureMetadataStructuredTable(group);
    return getMixtureMetadataStructuredTableAdapter().getMixtureId(
      metadataTable,
      evaluatedTableMap?.[metadataTable.name] ?? null
    );
  }

  // This exposes a single source that represents the group as a whole. Sub-tables are exposed for propagation separately.
  override getGroupSampleSources(
    group: MixtureGroup,
    evaluatedTableMap: OutputTableMap | null
  ): Array<SamplesSource> {
    const schemaId = this.getTagSchema(group)?.id;
    const sampleId = this.getMixtureId(group, evaluatedTableMap);
    const bioentityInfo = sampleId ? hydrateBioentityFromSampleId(sampleId) : null;
    const sample = bioentityInfo ? makeBioentitySampleDetails(bioentityInfo) : null;

    return schemaId && sample
      ? [
          {
            name: group.name,
            icon: groupKindToIcon(NoteLinkedGroupKind.MIXTURE_GROUP),
            samplesByColumn: [
              {
                // This sample always comes from the EXISTING_BIOENTITY column of the mixture metadata table
                displayName: EXISTING_BIOENTITY_COLUMN_NAME,
                sampleFilter: {
                  type: SampleFilterType.BIOENTITY,
                  schemaId,
                },
                samples: [sample],
              },
            ],
            table: null,
          },
        ]
      : [];
  }

  getGroupSchemas(group: MixtureGroup): Array<Schema> {
    const mixtureSchema = this.getTagSchema(group);
    return mixtureSchema ? [mixtureSchema] : [];
  }
}
